import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../pages/customer/useCart';

const Navbar = ({ variant = 'default' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  
  // Mobile menu states
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
    navigate('/');
  };

  // Close mobile menu when clicking a link
  const handleNavClick = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const ROLE_CONFIG = {
    admin:    { label: '⚡ Admin',    route: '/admin',    btnText: 'Dashboard'    },
    farmer:   { label: '🌾 Farmer',   route: '/farmer',   btnText: 'My Portal'    },
    butcher:  { label: '🔪 Butcher',  route: '/butcher',  btnText: 'My Portal'    },
    customer: { label: '🛒 Customer', route: '/customer', btnText: 'My Dashboard' },
  };

  const PROFILE_TAB = {
  admin:    'settings',
  customer: 'profile',
  farmer:   'profile',
  butcher:  'profile',
};

  const roleInfo = user ? (ROLE_CONFIG[user.role] || ROLE_CONFIG.customer) : null;
  const { cart } = useCart(user);

  // Desktop auth buttons (unchanged)
  const authJSX = user ? (
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-2 hidden lg:flex">
        {user.profile_image ? (
          <img src={user.profile_image} alt="avatar"
               className="w-8 h-8 rounded-full object-cover border-2 border-primary/20" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-primary flex items-center
                          justify-center text-white font-bold text-sm">
            {(user.name || 'U')[0].toUpperCase()}
          </div>
        )}
        <div className="flex flex-col items-end">
          <span className="text-[10px] font-bold uppercase tracking-tighter
                           text-primary leading-none">
            {roleInfo.label}
          </span>
          <span className="text-sm font-bold text-on-surface">{user.name}</span>
        </div>
      </div>

      <button onClick={() => navigate(roleInfo.route)}
        className="hidden lg:block px-4 py-2 rounded-full bg-primary/10 text-primary font-bold text-sm
                   hover:bg-primary hover:text-white transition-all border border-primary/20">
        {roleInfo.btnText}
      </button>

      <button onClick={handleLogout}
        className="hidden lg:block px-5 py-2 rounded-full border-2 border-outline-variant text-on-surface
                   font-bold text-sm hover:bg-red-50 hover:text-red-600
                   hover:border-red-200 transition-all">
        Logout
      </button>
    </div>
  ) : (
    <div className="hidden lg:flex items-center gap-3">
      <button onClick={() => navigate('/login')}
        className="px-5 py-2 rounded-full border-2 border-primary text-primary
                   font-bold text-sm hover:bg-primary hover:text-white transition-all">
        Login
      </button>
      <button onClick={() => navigate('/register')}
        className="px-5 py-2 rounded-full bg-primary text-white font-bold text-sm
                   hover:bg-primary/90 transition-all shadow-md">
        Register
      </button>
    </div>
  );

  // ── Default Variant (Mobile-First) ──────────────────────────────────────
  if (variant === 'default') {
    return (
      <>
        <nav className="bg-white/80 dark:bg-zinc-950/80 backdrop-blur-md fixed top-0 w-full
                        z-50 shadow-sm border-b border-outline-variant/20">
          <div className="flex items-center gap-3 px-4 md:px-8 h-16 md:h-20
                          max-w-screen-2xl mx-auto w-full">

            {/* Mobile: Hamburger Menu */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 -ml-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors"
              aria-label="Menu">
              <span className="material-symbols-outlined text-2xl">
                {mobileMenuOpen ? 'close' : 'menu'}
              </span>
            </button>

            {/* Logo */}
            <div className="cursor-pointer h-10 md:h-12 flex items-center ml-2" onClick={() => handleNavClick('/')}>
              <img src="/assets/elite-goats.png" alt="Logo"
                  className="h-10 md:h-12 w-auto object-contain" />
            </div>

            {/* Desktop Navigation Links */}
            <div className="hidden md:flex items-center gap-8">
              <a onClick={() => navigate('/')}
                className={`font-semibold cursor-pointer transition-colors ${
                  location.pathname === '/'
                    ? 'text-primary border-b-2 border-primary pb-1'
                    : 'text-on-surface-variant hover:text-primary'
                }`}>
                Home
              </a>
              <a onClick={() => navigate('/search')}
                className={`font-semibold cursor-pointer transition-colors ${
                  location.pathname === '/search' || location.pathname.startsWith('/product/')
                    ? 'text-primary border-b-2 border-primary pb-1'
                    : 'text-on-surface-variant hover:text-primary'
                }`}>
                Marketplace
              </a>
              <a onClick={() => navigate('/breeders')}
                className={`font-semibold cursor-pointer transition-colors ${
                  location.pathname === '/breeders'
                    ? 'text-primary border-b-2 border-primary pb-1'
                    : 'text-on-surface-variant hover:text-primary'
                }`}>
                Breeders
              </a>
              <a onClick={() => navigate('/auctions')}
                className={`font-semibold cursor-pointer transition-colors ${
                  location.pathname === '/auctions'
                    ? 'text-primary border-b-2 border-primary pb-1'
                    : 'text-on-surface-variant hover:text-primary'
                }`}>
                Auctions
              </a>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-2 md:gap-3 ml-auto">
              {/* Search - Mobile only */}
              <button 
                onClick={() => navigate('/search')} 
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors">
                <span className="material-symbols-outlined text-xl">search</span>
              </button>

              {/* Wishlist - Desktop */}
              <button 
                onClick={() => navigate('/wishlist')} 
                className="hidden md:block p-2 hover:bg-surface-container rounded-full">
                <span className="material-symbols-outlined">favorite</span>
              </button>

              {/* Cart - Desktop */}
              <button 
                onClick={() => {
                  const cartRoute = roleInfo?.route ? `${roleInfo.route}#cart` : '/customer#cart';
                  navigate(cartRoute);
                }}
                className="hidden md:block p-2 hover:bg-surface-container rounded-full relative">
                <span className="material-symbols-outlined">shopping_bag</span>
                {cart && cart.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-white text-[10px] font-black rounded-full flex items-center justify-center">
                    {cart.length}
                  </span>
                )}
              </button>

              {/* User Menu - Mobile */}
              <div className="md:hidden relative">
                <button 
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors">
                  <span className="material-symbols-outlined text-xl">
                    {user ? 'account_circle' : 'person'}
                  </span>
                </button>

                {/* Mobile User Dropdown */}
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                    {user ? (
                      <>
                        <div className="px-4 py-2 border-b border-gray-100">
                          <p className="font-semibold text-sm">{user.name}</p>
                          <p className="text-xs text-gray-500">Signed in</p>
                        </div>
                        <button onClick={() => { 
                          navigate(`${roleInfo.route}#${PROFILE_TAB[user.role] || 'profile'}`);
                          setUserMenuOpen(false); 
                        }} 
                          className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2 text-sm">
                          <span className="material-symbols-outlined text-lg">person</span>
                          My Profile
                        </button>
                        {roleInfo?.route && (
                          <button onClick={() => { navigate(roleInfo.route); setUserMenuOpen(false); }} 
                            className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2 text-sm">
                            <span className="material-symbols-outlined text-lg">dashboard</span>
                            {roleInfo.btnText}
                          </button>
                        )}
                        <hr className="my-2" />
                        <button onClick={handleLogout} 
                          className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2 text-red-600 text-sm">
                          <span className="material-symbols-outlined text-lg">logout</span>
                          Logout
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => { navigate('/login'); setUserMenuOpen(false); }} 
                          className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2 text-sm">
                          <span className="material-symbols-outlined text-lg">login</span>
                          Login
                        </button>
                        <button onClick={() => { navigate('/register'); setUserMenuOpen(false); }} 
                          className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2 text-sm">
                          <span className="material-symbols-outlined text-lg">person_add</span>
                          Register
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Desktop Auth */}
              {authJSX}
            </div>

          </div>
        </nav>

        {/* ═══════════════════════════════════════════════════ */}
        {/* MOBILE HAMBURGER MENU (Slide-out Drawer) */}
        {/* ═══════════════════════════════════════════════════ */}
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="md:hidden fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
              onClick={() => setMobileMenuOpen(false)}
            />
            
            {/* Drawer */}
            <div className="md:hidden fixed top-0 left-0 bottom-0 w-80 max-w-[85vw] bg-white z-50 shadow-2xl overflow-y-auto">
              
              {/* Header */}
              <div className="bg-primary text-white p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <img src="/assets/elite-goats.png" alt="Logo"
                        className="h-10 w-auto object-contain brightness-0 invert" />
                  </div>
                  <button 
                    onClick={() => setMobileMenuOpen(false)}
                    className="p-2 rounded-lg hover:bg-white/10">
                    <span className="material-symbols-outlined">close</span>
                  </button>
                </div>
                {user && (
                  <div className="flex items-center gap-3 bg-white/10 rounded-lg p-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <span className="material-symbols-outlined">person</span>
                    </div>
                    <div>
                      <p className="font-semibold">{user.name}</p>
                      <p className="text-xs text-white/70">{roleInfo?.label || 'Customer'}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Menu Items */}
              <div className="py-2">
                {/* Main Navigation */}
                <div className="px-4 py-2">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Navigate</p>
                </div>
                
                <button onClick={() => handleNavClick('/')} 
                  className="w-full text-left px-6 py-3 hover:bg-gray-50 flex items-center gap-3">
                  <span className="material-symbols-outlined text-primary">home</span>
                  <span className="font-medium">Home</span>
                </button>

                <button onClick={() => handleNavClick('/search')} 
                  className="w-full text-left px-6 py-3 hover:bg-gray-50 flex items-center gap-3">
                  <span className="material-symbols-outlined text-primary">explore</span>
                  <span className="font-medium">Browse Marketplace</span>
                </button>

                <button onClick={() => handleNavClick('/search?mode=meat')} 
                  className="w-full text-left px-6 py-3 hover:bg-gray-50 flex items-center gap-3">
                  <span className="material-symbols-outlined text-red-600">restaurant</span>
                  <span className="font-medium">Meat Products</span>
                </button>

                <button onClick={() => handleNavClick('/auctions')} 
                  className="w-full text-left px-6 py-3 hover:bg-gray-50 flex items-center gap-3">
                  <span className="material-symbols-outlined text-orange-600">gavel</span>
                  <span className="font-medium">Live Auctions</span>
                </button>

                <button onClick={() => handleNavClick('/breeders')} 
                  className="w-full text-left px-6 py-3 hover:bg-gray-50 flex items-center gap-3">
                  <span className="material-symbols-outlined text-green-600">verified_user</span>
                  <span className="font-medium">Verified Breeders</span>
                </button>

                {/* Account Section */}
                {user && (
                  <>
                    <div className="px-4 py-2">
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">My Account</p>
                    </div>

                    <button onClick={() => handleNavClick(`${roleInfo.route}#${PROFILE_TAB[user.role] || 'profile'}`)} 
                      className="w-full text-left px-6 py-3 hover:bg-gray-50 flex items-center gap-3">
                      <span className="material-symbols-outlined text-gray-600">person</span>
                      <span className="font-medium">My Profile</span>
                    </button>
                    
                    {roleInfo?.route && (
                      <button onClick={() => handleNavClick(roleInfo.route)} 
                        className="w-full text-left px-6 py-3 hover:bg-gray-50 flex items-center gap-3">
                        <span className="material-symbols-outlined text-gray-600">dashboard</span>
                        <span className="font-medium">{roleInfo.btnText}</span>
                      </button>
                    )}

                    <hr className="my-3" />
                  </>
                )}
              </div>

              {/* Bottom Actions */}
              <div className="p-4 border-t border-gray-200 bg-gray-50">
                {!user ? (
                  <div className="space-y-2">
                    <button 
                      onClick={() => handleNavClick('/login')}
                      className="w-full py-3 bg-primary text-white rounded-xl font-bold hover:bg-primary/90 transition-colors">
                      Login
                    </button>
                    <button 
                      onClick={() => handleNavClick('/register')}
                      className="w-full py-3 bg-white text-primary border-2 border-primary rounded-xl font-bold hover:bg-gray-50 transition-colors">
                      Register
                    </button>
                  </div>
                ) : (
                  <button 
                    onClick={handleLogout}
                    className="w-full py-3 bg-red-50 text-red-600 rounded-xl font-bold hover:bg-red-100 transition-colors flex items-center justify-center gap-2">
                    <span className="material-symbols-outlined">logout</span>
                    Logout
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  // ── Premium Variant ──────────────────────────────────────
  return (
    <nav className="fixed top-0 w-full z-50 bg-white/70 backdrop-blur-xl
                    border-b border-slate-100">
      <div className="flex justify-between items-center h-20 px-6 lg:px-12
                      max-w-screen-2xl mx-auto w-full">

        <div className="flex items-center gap-4 cursor-pointer h-12"
             onClick={() => navigate('/')}>
          <img src="/assets/elite-goats.png" alt="Logo"
               className="h-12 w-auto object-contain" />
        </div>

        <div className="hidden lg:flex items-center gap-10">
          <a onClick={() => navigate('/search')}
             className="text-sm font-semibold text-slate-500 uppercase cursor-pointer">
            Marketplace
          </a>
          <a href="#" className="text-sm font-bold text-primary border-b-2
                                 border-primary pb-1 uppercase">
            Breeders
          </a>
        </div>

        <div className="flex items-center gap-6">
          <button className="hidden sm:block text-sm font-bold text-primary px-6 py-2
                             rounded-full border border-primary hover:bg-primary/5">
            List Stock
          </button>
          {authJSX}
        </div>

      </div>
    </nav>
  );
};

export default Navbar;